import * as THREE from 'three';
import { COURT, REDE } from '../config';
import { VAO_DA_REDE } from './tecidoDaRede';
import { clamp } from '../core/math';

/** Lado da quadra. Home e' o jogador (Z negativo), Away e' a CPU (Z positivo). */
export type Side = 'home' | 'away';

export const oposto = (lado: Side): Side => (lado === 'home' ? 'away' : 'home');

/** Sinal do eixo Z local associado ao lado: home = -1, away = +1. */
export const sinalDe = (lado: Side): number => (lado === 'home' ? -1 : 1);

const _local = new THREE.Vector3();
const _local2 = new THREE.Vector3();

/**
 * A quadra. E' a UNICA fonte de verdade sobre geometria de jogo: limites,
 * lados, rede, pontos de saque e spawn.
 *
 * Tudo e' calculado em espaco LOCAL e convertido pra mundo por uma matriz. Isso
 * nao e' preciosismo: e' o que permite mover e girar a quadra sem tocar em
 * nenhuma linha de bola, jogador ou IA — e, mais pra frente, ter varias quadras
 * espalhadas por um mundo aberto, cada uma com medidas proprias.
 *
 * A classe nao importa nada de render. A geometria visual e' construida em
 * buildCourt.ts, a partir DESTES mesmos numeros: e' impossivel o que se ve'
 * divergir do que colide.
 */
export class Court {
  /** Transformacao da quadra no mundo. Hoje identidade; amanha, uma entre varias. */
  readonly matrix = new THREE.Matrix4();
  private readonly matrixInv = new THREE.Matrix4();

  /**
   * A rotacao da quadra, separada da matriz.
   *
   * Velocidade se converte por ROTACAO, nunca por transformDirection: aquele
   * normaliza o resultado, e uma bola a 20 m/s viraria uma bola a 1 m/s sem
   * erro de tipo nenhum pra avisar.
   */
  readonly quaternion = new THREE.Quaternion();
  readonly quaternionInv = new THREE.Quaternion();

  readonly halfLength = COURT.length / 2;
  readonly halfWidth = COURT.width / 2;

  /** Meia quadra + zona livre: o limite de corrida dos atletas. */
  readonly halfLengthFree = COURT.length / 2 + COURT.freeZone;
  readonly halfWidthFree = COURT.width / 2 + COURT.freeZone;

  /**
   * Vao entre o atleta e a rede, pra ALVO: onde a IA para, onde se mira.
   *
   * O corpo passa dele e entra na malha, que cede e empurra de volta — ver
   * `limitarCorpo` e `molaDoCorpo`, que usa o mesmo numero.
   */
  static readonly NET_GAP = VAO_DA_REDE;

  constructor(posicao = new THREE.Vector3(), rotacaoY = 0) {
    this.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rotacaoY);
    this.quaternionInv.copy(this.quaternion).invert();
    this.matrix.compose(posicao, this.quaternion, new THREE.Vector3(1, 1, 1));
    this.matrixInv.copy(this.matrix).invert();
  }

  paraMundo(local: THREE.Vector3, out = new THREE.Vector3()): THREE.Vector3 {
    return out.copy(local).applyMatrix4(this.matrix);
  }

  paraLocal(mundo: THREE.Vector3, out = new THREE.Vector3()): THREE.Vector3 {
    return out.copy(mundo).applyMatrix4(this.matrixInv);
  }

  /** Y do chao da quadra, em mundo. */
  get floorY(): number {
    return this.matrix.elements[13]!;
  }

  /** Altura do topo da rede, em mundo. */
  get netTopY(): number {
    return this.floorY + COURT.netHeight;
  }

  /** Em que lado da quadra esta' este ponto do mundo? */
  ladoDe(mundo: THREE.Vector3): Side {
    return this.paraLocal(mundo, _local).z < 0 ? 'home' : 'away';
  }

  /** Distancia com sinal ate' o plano da rede, em espaco local. */
  distanciaAteRede(mundo: THREE.Vector3): number {
    return this.paraLocal(mundo, _local).z;
  }

  /** Esta' dentro das linhas? A linha conta como dentro. */
  dentroDaQuadra(mundo: THREE.Vector3, tolerancia = 0): boolean {
    this.paraLocal(mundo, _local);
    return (
      Math.abs(_local.x) <= this.halfWidth + tolerancia &&
      Math.abs(_local.z) <= this.halfLength + tolerancia
    );
  }

  /**
   * Traz o alvo do JOGADOR pra area em que faz sentido mirar.
   *
   * A diferenca pra `limitarMira` e' tudo: aquela prende o alvo DENTRO das
   * linhas, com recuo, e existe pra IA — um bot que mira em cima da linha erra
   * pra fora metade das vezes e vira um adversario burro.
   *
   * Pro jogador isso era o contrario de dificuldade. Com o alvo preso 40 cm
   * dentro da quadra, mirar na linha nao era arriscado: era IMPOSSIVEL. Nenhuma
   * bola saia por escolha, so' por erro — e sem risco na mira, atacar no canto
   * custa o mesmo que atacar no meio.
   *
   * Agora a mira alcanca a zona livre inteira: da' pra mirar em cima da linha,
   * e da' pra mirar fora. O que a area ainda impede e' o absurdo — o raio do
   * mouse cruza um plano de 400 m, e mirar no horizonte nao e' uma jogada.
   */
  limitarMiraDoJogador(mundo: THREE.Vector3, lado: Side, out = new THREE.Vector3()): THREE.Vector3 {
    return this.limitarMira(mundo, lado, -COURT.freeZone, out);
  }

  /**
   * Quantos metros este ponto esta' FORA das linhas. Zero ou menos: dentro.
   *
   * E' a mesma pergunta de `dentroDaQuadra`, com a resposta em metros em vez de
   * sim/nao — quem julga bola fora precisa saber por QUANTO, porque a margem
   * de erro de quem julga e' uma distancia.
   */
  distanciaParaFora(mundo: THREE.Vector3): number {
    this.paraLocal(mundo, _local);
    return Math.max(
      Math.abs(_local.x) - this.halfWidth,
      Math.abs(_local.z) - this.halfLength,
    );
  }

  /**
   * Ponto no chao de um dos lados.
   * `normalizedX`: -1 (lateral esquerda) a 1 (direita).
   * `depth01`: 0 = colado na rede, 1 = linha de fundo.
   */
  pontoDaQuadra(lado: Side, normalizedX: number, depth01: number, out = new THREE.Vector3()): THREE.Vector3 {
    const x = clamp(normalizedX, -1, 1) * this.halfWidth;
    const z = sinalDe(lado) * clamp(depth01, 0, 1) * this.halfLength;
    return this.paraMundo(_local2.set(x, 0, z), out);
  }

  /** Posicao base do atleta no seu lado. */
  posicaoDeSpawn(lado: Side, out = new THREE.Vector3()): THREE.Vector3 {
    return this.pontoDaQuadra(lado, 0, COURT.spawnDepthRatio, out);
  }

  /** Onde o sacador se posiciona: atras da linha de fundo. */
  posicaoDeSaque(lado: Side, out = new THREE.Vector3()): THREE.Vector3 {
    const z = sinalDe(lado) * (this.halfLength + COURT.serveBackOffset);
    return this.paraMundo(_local2.set(0, 0, z), out);
  }

  /** Pra onde o atleta olha quando encara a rede. */
  direcaoParaRede(lado: Side, out = new THREE.Vector3()): THREE.Vector3 {
    // -sinal porque home (Z negativo) precisa olhar pra Z positivo.
    return out.set(0, 0, -sinalDe(lado)).transformDirection(this.matrix);
  }

  /**
   * Traz um alvo de mira pra dentro das linhas do lado indicado.
   *
   * O recuo evita mirar em cima da linha, onde meio metro de erro vira bola
   * fora. E o minimo de 0.8 em Z evita mirar colado na rede, que na pratica e'
   * jogar a bola na propria fita.
   */
  limitarMira(mundo: THREE.Vector3, lado: Side, recuo = 0.4, out = new THREE.Vector3()): THREE.Vector3 {
    this.paraLocal(mundo, _local);

    const maxX = Math.max(0.2, this.halfWidth - recuo);
    const maxZ = Math.max(0.4, this.halfLength - recuo);
    _local.x = clamp(_local.x, -maxX, maxX);
    _local.z = sinalDe(lado) > 0
      ? clamp(_local.z, 0.8, maxZ)
      : clamp(_local.z, -maxZ, -0.8);
    _local.y = 0;

    return this.paraMundo(_local, out);
  }

  /**
   * Limita a posicao do SACADOR: atras da linha de fundo.
   *
   * No volei, sacar com o pe' dentro da quadra e' falta. Sem este limite o
   * jogador anda ate' a rede com a bola na mao e saca de la', o que nao e'
   * saque nenhum — e' um ataque de graca, sem ninguem podendo defender.
   *
   * A lateral continua sendo a zona livre inteira: atras da linha de fundo, o
   * sacador pode se posicionar onde quiser, e isso e' regra de verdade.
   */
  limitarAreaDeSaque(mundo: THREE.Vector3, lado: Side, out = new THREE.Vector3()): THREE.Vector3 {
    this.paraLocal(mundo, _local);

    _local.x = clamp(_local.x, -this.halfWidthFree, this.halfWidthFree);
    _local.z = lado === 'home'
      ? clamp(_local.z, -this.halfLengthFree, -this.halfLength)
      : clamp(_local.z, this.halfLength, this.halfLengthFree);

    return this.paraMundo(_local, out);
  }

  /**
   * Limita uma posicao a' area de corrida de um lado: meia quadra + zona livre,
   * parando antes da rede.
   *
   * Isto substitui a IMovementArea do Unity. A abstracao existia pra que o
   * motor do atleta nao conhecesse a quadra — e continua valendo: o Motor
   * recebe uma funcao de clamp, nao a quadra.
   */
  limitarArea(mundo: THREE.Vector3, lado: Side, out = new THREE.Vector3()): THREE.Vector3 {
    this.paraLocal(mundo, _local);

    _local.x = clamp(_local.x, -this.halfWidthFree, this.halfWidthFree);
    _local.z = lado === 'home'
      ? clamp(_local.z, -this.halfLengthFree, -Court.NET_GAP)
      : clamp(_local.z, Court.NET_GAP, this.halfLengthFree);

    return this.paraMundo(_local, out);
  }

  /**
   * Limita o CORPO de quem joga: a mesma area de `limitarArea`, mas deixando o
   * corpo entrar na rede.
   *
   * `limitarArea` para a 35 cm do plano, e continua sendo o que vale pra ALVO
   * (pra onde a IA corre, onde se mira): ninguem escolhe ficar dentro da rede.
   * O corpo, nao — quem chega correndo passa do ponto e entra na malha, que
   * cede e empurra de volta (`Arena.encostarNaRede`). Aqui so' mora o fundo:
   * o centro do corpo nunca passa de `folgaMinima` do plano.
   *
   * Perto dos postes o fundo volta aos 35 cm, numa rampa: ali nao ha' malha
   * pra ceder, e o corpo entraria no poste.
   */
  limitarCorpo(mundo: THREE.Vector3, lado: Side, out = new THREE.Vector3()): THREE.Vector3 {
    this.paraLocal(mundo, _local);

    _local.x = clamp(_local.x, -this.halfWidthFree, this.halfWidthFree);
    const dosPostes = REDE.meiaLargura - Math.abs(_local.x);
    const t = clamp((dosPostes - 0.05) / 0.5, 0, 1);
    const folga = Court.NET_GAP + (REDE.corpo.folgaMinima - Court.NET_GAP) * t;
    _local.z = lado === 'home'
      ? clamp(_local.z, -this.halfLengthFree, -folga)
      : clamp(_local.z, folga, this.halfLengthFree);

    return this.paraMundo(_local, out);
  }

  /**
   * Empurra um corpo pra fora da rede e dos postes desta quadra.
   *
   * Quem joga nunca precisou disto: o limite de area ja' o prende na propria
   * metade, com folga pra rede. Quem ANDA pela praia atravessa as quadras, e
   * uma rede que se atravessa a pe' desmonta o lugar inteiro — nada mais ali
   * parece solido depois disso.
   *
   * E' empurrao, nao clamp: quem entra pela frente sai pela frente. Um clamp
   * escolheria sempre o mesmo lado e teleportaria quem chega pelo outro.
   *
   * Nao vale pros corpos dos atletas nem pra bola. Os atletas sao pequenos e se
   * mexem, e atravessar um de raspao le' como dois jogadores disputando a bola;
   * atravessar a rede nao le' como nada.
   */
  desviarDaRede(
    mundo: THREE.Vector3,
    raio: number,
    out = new THREE.Vector3(),
    /** Distancia minima do centro ao plano da rede. Sem ela, espessura + raio. */
    folgaDaRede = COURT.netThickness / 2 + raio,
  ): THREE.Vector3 {
    this.paraLocal(mundo, _local);
    let empurrou = false;

    const meiaEspessura = folgaDaRede;
    if (Math.abs(_local.x) <= this.halfWidth + raio && Math.abs(_local.z) < meiaEspessura) {
      // z === 0 exato nao tem lado: manda pro campo away, que e' uma escolha
      // qualquer mas precisa ser uma.
      _local.z = (_local.z < 0 ? -1 : 1) * meiaEspessura;
      empurrou = true;
    }

    const posteX = this.halfWidth + COURT.postOffset;
    const alcance = COURT.postRadius + raio;
    for (const sinal of [-1, 1]) {
      const dx = _local.x - sinal * posteX;
      const dz = _local.z;
      const distancia = Math.hypot(dx, dz);
      if (distancia >= alcance) continue;

      // Em cima do poste, empurra pra fora pela lateral: nao ha' direcao.
      if (distancia < 1e-4) {
        _local.x = sinal * (posteX + alcance);
      } else {
        const k = alcance / distancia;
        _local.x = sinal * posteX + dx * k;
        _local.z = dz * k;
      }
      empurrou = true;
    }

    /**
     * Sem contato, devolve a posicao COMO ELA ESTAVA — sem passar pela volta
     * mundo -> local -> mundo.
     *
     * Nao e' economia, e' correcao. A volta e' um par de multiplicacoes 4x4, e
     * numa quadra transladada ela devolve um valor diferente do original nos
     * ultimos bits: x + 26 - 26 nao e' x quando x e' 0,0032. O Motor compara a
     * posicao limitada com a original por igualdade EXATA pra saber se bateu
     * numa parede, e mata a velocidade naquele eixo quando difere. Com o erro
     * de volta, ele achava que batia na parede todo quadro: quem andava pelo
     * eixo X nunca passava de dois metros por segundo.
     */
    if (!empurrou) return out.copy(mundo);
    return this.paraMundo(_local, out);
  }
}
